/**
 * @file    EnterpriseContext.tsx
 * @scope   STATE
 * @summary The public state API for DIY Enterprise V2. Wraps `enterpriseReducer` in React Context
 *          and exposes raw `state`/`dispatch`, memoized convenience actions and derived selectors.
 * @consumes composeEngineReducer + Enterprise descriptor slice, partition validation
 * @produces `EnterpriseProvider`, `useEnterpriseContext`, `EnterpriseContextValue`
 * @sideEffects Notifies the parent (`onStateChange`) whenever the serialized state changes — used
 *              to mirror into the global Zustand store.
 * @notes WHY convenience actions: components dispatch through these stable `useCallback` callbacks
 *        instead of hand-building action objects — keeps action shapes in one place and avoids
 *        extra re-renders. Add new state behaviour in the reducer; expose it here.
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import {
  FieldsManagerViewOptionsModel,
  SelectedFieldModel,
  SelectedUserFields,
} from 'models/Enterprise/FieldsManagerModel';
import { EnterpriseAction } from './enterpriseReducer';
import { isEnterpriseDataInvalid } from '../utils/validation/partitionValidation';
import {
  composeEngineReducer,
  createEngineState,
} from '../../../engine/state/composeEngineReducer';
import { ENTERPRISE_DESCRIPTOR } from '../descriptor';

// WHY module-level: one stable reducer reference for `useReducer`.
const engineReducer = composeEngineReducer(ENTERPRISE_DESCRIPTOR);

// --- Context Value ---

interface EnterpriseContextValue {
  state: EnterpriseGlobalModel;
  dispatch: React.Dispatch<EnterpriseAction>;

  // Convenience actions
  setPartitions: (partitions: PartitionModel[]) => void;
  setEnterpriseFields: (fields: SelectedUserFields[]) => void;
  updateUserFields: (fields: SelectedUserFields) => void;
  /** Atomically drops incomplete fields/filters from a partition against the freshest state. */
  stripIncompleteFields: (partitionGuid: string) => void;
  setViewOptions: (options: FieldsManagerViewOptionsModel) => void;
  updateFieldInPartition: (
    partitionGuid: string,
    fieldIndex: number,
    updatedField: SelectedFieldModel
  ) => void;
  syncPartitionsWithFields: (partitions: PartitionModel[]) => void;
  clearSelectedValues: () => void;

  // Derived state / selectors
  isInvalid: (customWidgetConfig: any) => boolean;
  getAdvancedSettingsField: (
    partitionGuid: string | undefined,
    fieldIndex: number | undefined
  ) => SelectedFieldModel | null;
}

const EnterpriseContext = createContext<EnterpriseContextValue | null>(null);

// --- Provider ---

interface EnterpriseProviderProps {
  children: React.ReactNode;
  initialState?: EnterpriseGlobalModel;
  onStateChange?: (state: EnterpriseGlobalModel) => void;
}

export const EnterpriseProvider: React.FC<EnterpriseProviderProps> = ({
  children,
  initialState,
  onStateChange,
}) => {
  // WHY: composed engine state (core + Enterprise slice, §9). The public API keeps exposing the
  // Enterprise slice as `state`, so consumers are unchanged.
  const [engineState, dispatch] = useReducer(engineReducer, undefined, () =>
    createEngineState(
      ENTERPRISE_DESCRIPTOR.id,
      initialState ?? ENTERPRISE_DESCRIPTOR.stateSlice.initialState()
    )
  );
  const state = engineState.slice;

  // Notify parent of state changes (for Zustand sync)
  const prevSerializedRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serialized !== prevSerializedRef.current) {
      prevSerializedRef.current = serialized;
      onStateChange?.(state);
    }
  }, [state, onStateChange]);

  // Convenience action creators
  const setPartitions = useCallback(
    (partitions: PartitionModel[]) => dispatch({ type: 'SET_PARTITIONS', partitions }),
    []
  );

  const setEnterpriseFields = useCallback(
    (fields: SelectedUserFields[]) => dispatch({ type: 'SET_ENTERPRISE_FIELDS', fields }),
    []
  );

  const updateUserFields = useCallback(
    (fields: SelectedUserFields) => dispatch({ type: 'UPDATE_USER_FIELDS', fields }),
    []
  );

  const stripIncompleteFields = useCallback(
    (partitionGuid: string) => dispatch({ type: 'STRIP_INCOMPLETE_FIELDS', partitionGuid }),
    []
  );

  const setViewOptions = useCallback(
    (options: FieldsManagerViewOptionsModel) => dispatch({ type: 'SET_VIEW_OPTIONS', options }),
    []
  );

  const updateFieldInPartitionAction = useCallback(
    (partitionGuid: string, fieldIndex: number, updatedField: SelectedFieldModel) =>
      dispatch({ type: 'UPDATE_FIELD_IN_PARTITION', partitionGuid, fieldIndex, updatedField }),
    []
  );

  const syncPartitionsWithFields = useCallback(
    (partitions: PartitionModel[]) => dispatch({ type: 'SYNC_PARTITIONS_WITH_FIELDS', partitions }),
    []
  );

  const clearSelectedValues = useCallback(() => dispatch({ type: 'CLEAR_SELECTED_VALUES' }), []);

  const isInvalid = useCallback(
    (customWidgetConfig: any) => isEnterpriseDataInvalid(state, customWidgetConfig),
    [state]
  );

  const getAdvancedSettingsField = useCallback(
    (
      partitionGuid: string | undefined,
      fieldIndex: number | undefined
    ): SelectedFieldModel | null => {
      if (!partitionGuid || fieldIndex === undefined) return null;
      return (
        state.selectedValues.selectedEnterpriseFields.find((f) => f.partitionGuid === partitionGuid)
          ?.fields[fieldIndex] ?? null
      );
    },
    [state.selectedValues.selectedEnterpriseFields]
  );

  const value: EnterpriseContextValue = {
    state,
    dispatch,
    setPartitions,
    setEnterpriseFields,
    updateUserFields,
    stripIncompleteFields,
    setViewOptions,
    updateFieldInPartition: updateFieldInPartitionAction,
    syncPartitionsWithFields,
    clearSelectedValues,
    isInvalid,
    getAdvancedSettingsField,
  };

  return <EnterpriseContext.Provider value={value}>{children}</EnterpriseContext.Provider>;
};

// --- Hook ---

export const useEnterpriseContext = (): EnterpriseContextValue => {
  const context = useContext(EnterpriseContext);
  if (!context) {
    throw new Error('useEnterpriseContext must be used within an EnterpriseProvider');
  }
  return context;
};

export default EnterpriseContext;
